import type { DebtData, PaymentConfiguration } from '@/types';
import type { OphelosPaymentPlanResponse } from '@/types/payment-plan';
import { creditApplied, creditClearsBalance } from './accountNet';
import { isAccountPaySnapshotDebt, isMultiCurrencyAccount, isOpenDebt, isUnpaidDebt } from './debtFilters';
import { activeAccountPayment, hasCaseLevelPlan, isPaymentPlanEligible } from './paymentPlans';

/**
 * Payment methods that support recurring/automatic charges. Account-scoped
 * plans are only enabled when the operator has at least one of these in
 * planPaymentMethods — Multibanco / MB Way account-level support is tracked
 * separately in #3873.
 */
const AUTOMATIC_PLAN_METHODS: ReadonlyArray<string> = ['card', 'sepa', 'bacs'];

/**
 * `none` sends the customer back to the dashboard; `blocked` renders the page with only the reason.
 * `payAll` is whether the dashboard shows the Pay all entry — narrower than `surface`, because a
 * live account payment opens the page below two debts, but only to be cancelled.
 */
export type AccountPaymentSurface =
  | { surface: 'none'; payAll: false }
  | { surface: 'blocked'; payAll: boolean; blockedBy: 'scheduled_account_payment' | 'account_plan' | 'case_plan' }
  | { surface: 'open'; payAll: true };

export type AccountPaymentTabs = { full: true; partial: boolean; plan: boolean };

export type AccountPaymentOffer =
  | Exclude<AccountPaymentSurface, { surface: 'open' }>
  | { surface: 'open'; payAll: true; tabs: AccountPaymentTabs };

/**
 * Whether the account has an account-wide payment surface — the part of
 * {@link accountPaymentOffer} that needs no config.
 *
 * `debts` is the portal's whole debt list, arranged and cleared debts included.
 */
export function accountPaymentSurface({
  debts,
  accountPaymentPlan,
}: {
  debts: DebtData[];
  accountPaymentPlan: OphelosPaymentPlanResponse | null;
}): AccountPaymentSurface {
  // Open, not payable: an arranged case must still reach the page to be told why.
  const openDebtCount = debts.filter(isOpenDebt).length;
  // The Connect snapshot population (positive, non-closed), so an arranged GBP debt still counts.
  const multiCurrency = isMultiCurrencyAccount(debts.filter(isAccountPaySnapshotDebt));
  const accountPayment = activeAccountPayment(accountPaymentPlan);

  // A scheduled account payment wins over a running account plan, which wins over a plan on
  // one debt. The case-plan bar covers the whole page: an immediate account payment is not
  // refused, and would allocate onto the arranged case unseen — #7474.
  const blockedBy =
    accountPayment === 'scheduled'
      ? 'scheduled_account_payment'
      : accountPayment === 'plan'
        ? 'account_plan'
        : hasCaseLevelPlan(debts)
          ? 'case_plan'
          : undefined;

  // A live account payment overrides the debt count only: the page is where it gets cancelled.
  if (multiCurrency || (openDebtCount < 2 && !accountPayment)) return { surface: 'none', payAll: false };
  if (blockedBy) return { surface: 'blocked', payAll: openDebtCount >= 2, blockedBy };
  return { surface: 'open', payAll: true };
}

/**
 * Whether the account has an account-wide payment surface, and what it offers.
 *
 * `debts` is the portal's whole debt list, arranged and cleared debts included — the count,
 * currency and tier gates each filter it differently. Tabs exist only on an open surface.
 */
export function accountPaymentOffer({
  debts,
  accountPaymentPlan,
  payment,
}: {
  debts: DebtData[];
  accountPaymentPlan: OphelosPaymentPlanResponse | null;
  payment: PaymentConfiguration;
}): AccountPaymentOffer {
  const surface = accountPaymentSurface({ debts, accountPaymentPlan });
  if (surface.surface !== 'open') return surface;

  // What an account payment would collect against — gross of credit, so a credit never hides
  // a tab the operator configured, except where it cleared the balance and only a zero charge is left.
  const unpaid = debts.filter(isUnpaidDebt);
  const totalBalance = unpaid.reduce((sum, d) => sum + d.currentBalance, 0);
  const nothingLeftToPay = creditClearsBalance(totalBalance, creditApplied(unpaid));

  const partial =
    payment.allowAccountPartialPayment &&
    payment.allowPartialPayment &&
    payment.partialPaymentTab &&
    !nothingLeftToPay &&
    totalBalance >= payment.minDebtForPartialPayment;
  // Whether a plan is on offer only. How long it can run is the plan tab's call, on the balance
  // after discounts and credit.
  const plan =
    payment.enablePaymentPlan &&
    // Configured and backed by an enabled provider: the plan tab filters its method picker on the
    // same pair, so the two must agree or it renders with nothing to pick.
    payment.planPaymentMethods.some(
      (m) => AUTOMATIC_PLAN_METHODS.includes(m) && payment.providers?.[m as keyof PaymentConfiguration['providers']]?.enabled,
    ) &&
    !nothingLeftToPay &&
    isPaymentPlanEligible(totalBalance, payment.paymentPlan);

  return { ...surface, tabs: { full: true, partial, plan } };
}
